import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = ["Coach", "B2B / Service Provider", "Agency Owner", "Course Creator", "Founder / SMB", "Other"];
const REVENUE_BANDS = ["Under ₹1L / month", "₹1L – ₹3L / month", "₹3L – ₹10L / month", "₹10L – ₹25L / month", "₹25L+ / month"];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_type: "",
    monthly_revenue: "", primary_goal: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short note.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        business_type: form.business_type || null,
        monthly_revenue: form.monthly_revenue || null,
        primary_goal: form.primary_goal.trim() || null,
      });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", business_type: "", monthly_revenue: "", primary_goal: "", message: "" });
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not submit form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <div className="eyebrow mb-6">Let's build your engine</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              Ready to build your <em className="gold-shine font-medium not-italic">Digital Sales Engine</em>?
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              Book a 30-minute growth strategy session to audit your current client acquisition system —
              and see exactly where you're losing revenue.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <div className="luxury-card p-7 md:p-10">
              {submitted ? (
                <div className="py-12 text-center" data-testid="contact-success">
                  <div className="font-serif-display text-4xl md:text-5xl mb-4">
                    <span className="gold-shine">Message received.</span>
                  </div>
                  <p className="text-zinc-400 max-w-md mx-auto font-body">
                    Thank you — we'll be in touch within 24 hours. If it's urgent, grab a slot on
                    Saurabh's calendar below.
                  </p>
                  <Link to="/book" className="btn-primary mt-8" data-testid="contact-success-calendly-btn">
                    Book a call <ArrowUpRight size={18} />
                  </Link>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="block mx-auto mt-4 text-sm text-zinc-500 hover:text-white transition"
                    data-testid="contact-send-another-btn"
                  >
                    Send another message →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Name *</label>
                      <input type="text" required value={form.name} onChange={update("name")} placeholder="Your full name" className="input-luxury" data-testid="contact-input-name" />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Email *</label>
                      <input type="email" required value={form.email} onChange={update("email")} placeholder="you@yourbrand.com" className="input-luxury" data-testid="contact-input-email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Phone / WhatsApp</label>
                      <input type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 00000 00000" className="input-luxury" data-testid="contact-input-phone" />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Business type</label>
                      <select value={form.business_type} onChange={update("business_type")} className="input-luxury" data-testid="contact-input-business-type">
                        <option value="">— Select —</option>
                        {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Current monthly revenue</label>
                    <select value={form.monthly_revenue} onChange={update("monthly_revenue")} className="input-luxury" data-testid="contact-input-monthly-revenue">
                      <option value="">— Select a range —</option>
                      {REVENUE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Primary goal</label>
                    <input type="text" value={form.primary_goal} onChange={update("primary_goal")} placeholder="e.g. 30+ qualified calls / month, launch a course, scale to ₹25L MRR…" className="input-luxury" data-testid="contact-input-primary-goal" />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">What's on your mind? *</label>
                    <textarea required rows={5} value={form.message} onChange={update("message")} placeholder="Tell us where you're stuck, and what a great outcome looks like for you…" className="input-luxury resize-none" data-testid="contact-input-message" />
                  </div>

                  <button
                    type="submit" disabled={submitting}
                    className="btn-primary w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? "Sending…" : "Send message"} <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="space-y-5">
              <div className="luxury-card p-7">
                <div className="eyebrow mb-5">Direct booking</div>
                <h3 className="font-serif-display text-3xl mb-3">Skip the queue.</h3>
                <p className="text-zinc-400 font-body leading-relaxed">
                  Grab a 30-minute slot directly on Saurabh's calendar — real strategy, not a sales pitch.
                </p>
                <Link to="/book" className="btn-primary mt-6 w-full justify-center" data-testid="contact-calendly-btn">
                  Book a Growth Audit <ArrowUpRight size={18} />
                </Link>
              </div>

              <div className="luxury-card p-7">
                <div className="eyebrow mb-5">Or reach us directly</div>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</div>
                      <a href="mailto:brandchilly@gmail.com" className="text-white hover:text-red-400 transition">brandchilly@gmail.com</a>
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#25D366]/10 border border-[#25D366]/25 flex items-center justify-center flex-shrink-0">
                      <MessageCircle size={16} className="text-[#25D366]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1.5">WhatsApp</div>
                      <a
                        href="https://wa.me/919811960890?text=Hi%20BrandChilly!%20I%27d%20like%20to%20know%20more."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#25D366] text-black text-sm font-medium px-4 py-2 hover:bg-[#2be072] transition-colors"
                        style={{ borderRadius: 0 }}
                        data-testid="contact-whatsapp-btn"
                      >
                        <MessageCircle size={14} /> Chat with us now
                      </a>
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Based in</div>
                      <div className="text-white">India · Worldwide remote</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
